import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronDown } from 'lucide-react';
import { AI_STYLES, getStyle } from '../config/aiStyles';

const AgentStyleSelector = ({
  selectedStyles,
  setSelectedStyles,
  descriptionSentences,
  setDescriptionSentences
}) => {
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStyleDropdown && !event.target.closest('.style-dropdown')) {
        setShowStyleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStyleDropdown]);

  // Get all styles except shuffle for selection
  const allStyles = Object.values(AI_STYLES).filter(style => style.key !== 'shuffle');

  const toggleStyle = (styleKey) => {
    if (selectedStyles.includes(styleKey)) {
      // Remove style if already selected (minimum 1 required)
      if (selectedStyles.length > 1) {
        setSelectedStyles(selectedStyles.filter(s => s !== styleKey));
      }
    } else {
      // Add style
      setSelectedStyles([...selectedStyles, styleKey]);
    }
  };

  const getSelectedStylesDisplay = () => {
    if (selectedStyles.length === 0) {
      return 'Select styles...';
    }
    if (selectedStyles.length === 1) {
      return getStyle(selectedStyles[0]).shortTitle;
    }
    return `${selectedStyles.length} styles selected`;
  };

  return (
    <div className="mt-6 bg-white p-6 rounded-lg shadow">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">How do you want your photo descriptions?</h3>
        <p className="text-gray-600">
          Select multiple styles and the agent will randomly pick from your selection for each photo
        </p>
      </div>
        
      {/* Style Selection Multi-Select */}
      <div className="mb-6">
        <label className="block text-lg font-semibold text-gray-800 mb-3">
          Choose Your Styles (Select Multiple)
        </label>
        
        <div className="relative style-dropdown">
          <button
            type="button"
            onClick={() => setShowStyleDropdown(!showStyleDropdown)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{getSelectedStylesDisplay()}</span>
                {selectedStyles.length > 0 && (
                  <span className="text-gray-500 ml-2">
                    {selectedStyles.length === 1 
                      ? `— ${getStyle(selectedStyles[0]).description}`
                      : `(Random selection from ${selectedStyles.length} styles)`}
                  </span>
                )}
              </div>
              <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showStyleDropdown ? 'rotate-180' : ''}`} />
            </div>
          </button>
          
          {showStyleDropdown && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto">
              {allStyles.map(style => {
                const isSelected = selectedStyles.includes(style.key);
                return (
                  <div
                    key={style.key}
                    onClick={() => toggleStyle(style.key)}
                    className={`cursor-pointer px-3 py-3 hover:bg-gray-50 border-l-4 transition-colors ${
                      isSelected
                        ? `${style.selectedBorderColor} bg-blue-50` 
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="text-lg mt-0.5">{style.emoji}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">
                            {style.shortTitle}
                            {style.popular && (
                              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                Most Popular
                              </span>
                            )}
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{style.description}</div>
                        <div className="text-xs text-gray-500 mt-1">Best for: {style.bestFor}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected Styles Summary */}
        {selectedStyles.length > 0 && (
          <div className="mt-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm font-semibold text-gray-900 mb-2">
                Selected Styles ({selectedStyles.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedStyles.map(styleKey => {
                  const style = getStyle(styleKey);
                  return (
                    <div
                      key={styleKey}
                      className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md border border-gray-200"
                    >
                      <span className="text-lg">{style.emoji}</span>
                      <span className="text-sm font-medium text-gray-900">{style.shortTitle}</span>
                      <button
                        onClick={() => {
                          if (selectedStyles.length > 1) {
                            setSelectedStyles(selectedStyles.filter(s => s !== styleKey));
                          }
                        }}
                        className="text-gray-400 hover:text-red-500 ml-1"
                        title={selectedStyles.length === 1 ? "At least one style must be selected" : "Remove style"}
                        disabled={selectedStyles.length === 1}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 text-xs text-gray-600">
                <strong>How it works:</strong> For each photo, the agent will randomly select one style from your selection above.
                This gives you variety while staying within your preferred style range.
              </div>
            </div>
          </div>
        )}

        {selectedStyles.length === 0 && (
          <div className="mt-4 text-sm text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            Please select at least one style to continue.
          </div>
        )}
      </div>

      {/* Description Length */}
      <div className="mb-6">
        <label className="block text-lg font-semibold text-gray-800 mb-3">Choose Detail Level</label>
        
        <div className="flex items-center space-x-4">
          {/* Quick Select Buttons */}
          <div className="flex space-x-2">
            {[
              { value: 1, label: 'Short' },
              { value: 3, label: 'Medium' },
              { value: 5, label: 'Long' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setDescriptionSentences(option.value)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  descriptionSentences === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Slider */}
          <div className="flex-1 max-w-32">
            <input
              type="range"
              min="1"
              max="5"
              value={descriptionSentences}
              onChange={(e) => setDescriptionSentences(parseInt(e.target.value))}
              className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Current Value */}
          <div className="text-sm text-gray-600 min-w-[4rem]">
            {descriptionSentences} sentence{descriptionSentences !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentStyleSelector;

